import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  BarChart3, FileDown, Loader2, Package, TrendingUp, Trash2, Star,
  Coffee, Wine, ChefHat,
} from "lucide-react";
import api, { formatApiError, API } from "../api";
import { Button, Card, Spinner, Badge, EmptyState } from "../components/ui/Primitives";
import { formatEUR as fmtEur } from "../utils/format";

const fmtPct = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} %`;

const REPORTS = [
  { key: "wareneinsatz",        icon: Package,    color: "#3B82F6" },
  { key: "food_cost_trend",     icon: ChefHat,    color: "#22c55e" },
  { key: "beverage_cost_trend", icon: Wine,       color: "#a78bfa" },
  { key: "waste",               icon: Trash2,     color: "#ef4444" },
  { key: "db_overview",         icon: TrendingUp, color: "#22c55e" },
  { key: "engineering_summary", icon: Star,       color: "#eab308" },
];

function downloadPdf(reportKey, label) {
  return async () => {
    try {
      const token = localStorage.getItem("studioops_token");
      const res = await fetch(`${API}/reports/${reportKey}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportKey}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF: ${label}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
}

function ReportCard({ reportKey, icon: Icon, color }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/${reportKey}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [reportKey]);
  useEffect(() => { load(); }, [load]);

  const handleDownload = async () => {
    setBusy(true);
    await downloadPdf(reportKey, t(`reports.${reportKey}_title`))();
    setBusy(false);
  };

  return (
    <Card className="p-4 space-y-3" data-testid={`report-${reportKey}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: color + "20" }}>
            <Icon size={14} style={{ color }} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink-primary truncate">{t(`reports.${reportKey}_title`)}</div>
            <div className="text-[11px] text-ink-muted">{t(`reports.${reportKey}_sub`)}</div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleDownload} disabled={busy} data-testid={`report-pdf-${reportKey}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
          PDF
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6"><Spinner size={16} /></div>
      ) : !data ? null : (
        <div data-testid={`report-summary-${reportKey}`}>
          <ReportSummary reportKey={reportKey} data={data} />
        </div>
      )}
    </Card>
  );
}

function ReportSummary({ reportKey, data }) {
  const { t } = useTranslation();
  if (reportKey === "wareneinsatz") {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-bg-void rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("reports.kpi_total_cost")}</div>
            <div className="num text-base font-medium">{fmtEur(data.total_cost)}</div>
          </div>
          <div className="bg-bg-void rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("reports.kpi_units")}</div>
            <div className="num text-base font-medium">{data.total_units}</div>
          </div>
        </div>
        {data.by_category.slice(0, 5).map((c) => (
          <div key={c.category} className="flex items-center justify-between text-xs px-1">
            <span className="text-ink-secondary truncate">{c.category}</span>
            <span className="num text-ink-primary">{fmtEur(c.cost)}</span>
          </div>
        ))}
      </div>
    );
  }
  if (reportKey === "food_cost_trend" || reportKey === "beverage_cost_trend") {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-ink-muted">{t("reports.kpi_avg")}</span>
          <span className="num font-medium text-ink-primary">{fmtPct(data.current_avg_pct)}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-ink-muted">{t("reports.kpi_target")}</span>
          <span className="num text-ink-secondary">{fmtPct(data.target_pct)}</span>
        </div>
        <div className="flex h-12 items-end gap-1">
          {data.buckets.map((b, i) => (
            <div key={i} className="flex-1 bg-brand/30 rounded-t-sm" style={{ height: `${Math.min(100, (b.value / 60) * 100)}%` }} title={`${b.label}: ${fmtPct(b.value)}`} />
          ))}
        </div>
        <div className="flex justify-between text-[9px] text-ink-muted">
          {data.buckets.map((b, i) => <span key={i}>{b.label.split(" ")[0]}</span>)}
        </div>
      </div>
    );
  }
  if (reportKey === "waste") {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-ink-muted">{t("reports.kpi_losses")}</span>
          <span className="num font-medium text-err">{fmtEur(data.total_cost)}</span>
        </div>
        {(data.top_items || []).slice(0, 3).map((it, i) => (
          <div key={i} className="flex items-center justify-between text-xs px-1">
            <span className="truncate">{it.name}</span>
            <span className="num text-ink-secondary">{fmtEur(it.cost)}</span>
          </div>
        ))}
        {data.top_items.length === 0 && <div className="text-[11px] text-ink-muted">{t("reports.no_data")}</div>}
      </div>
    );
  }
  if (reportKey === "db_overview") {
    return (
      <div className="space-y-1">
        {data.rows.slice(0, 5).map((r, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="text-ink-secondary truncate flex-1">{i + 1}. {r.name}</span>
            <span className="num text-ok">{fmtEur(r.total_db)}</span>
          </div>
        ))}
        {data.rows.length === 0 && <div className="text-[11px] text-ink-muted">{t("reports.no_data")}</div>}
      </div>
    );
  }
  if (reportKey === "engineering_summary") {
    const c = data.counts;
    return (
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-ok/10 rounded-md px-2 py-1.5"><Badge tone="ok" className="text-[10px]">★ Stars</Badge> <span className="num ml-1">{c.star}</span></div>
        <div className="bg-blue-500/10 rounded-md px-2 py-1.5"><Badge tone="default" className="text-[10px]">Plowhorses</Badge> <span className="num ml-1">{c.plowhorse}</span></div>
        <div className="bg-warn/10 rounded-md px-2 py-1.5"><Badge tone="warn" className="text-[10px]">Puzzles</Badge> <span className="num ml-1">{c.puzzle}</span></div>
        <div className="bg-err/10 rounded-md px-2 py-1.5"><Badge tone="err" className="text-[10px]">Dogs</Badge> <span className="num ml-1">{c.dog}</span></div>
      </div>
    );
  }
  return null;
}

export default function ReportsPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 fade-in" data-testid="reports-page">
      <header>
        <div className="text-[11px] uppercase tracking-wider text-brand mb-1 flex items-center gap-2">
          <BarChart3 size={12} /> {t("reports.kicker")}
        </div>
        <h1 className="text-2xl font-semibold text-ink-primary">{t("reports.title")}</h1>
        <p className="text-sm text-ink-secondary mt-1 max-w-2xl">{t("reports.subtitle")}</p>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {REPORTS.map((r) => <ReportCard key={r.key} reportKey={r.key} icon={r.icon} color={r.color} />)}
      </div>
    </div>
  );
}
